/**
 * Shared form validation utility.
 *
 * Error messages are returned as i18n `{ key, params? }` objects so the UI can
 * render them through `useTranslate('shared.campaignValidation')`. Keys map to
 * resources defined in `src/i18n/shared/campaignValidation.puff.json`.
 *
 * Common (cross-campaign-type) validators live here; campaign-type-specific
 * validators are in dedicated modules:
 *   - ncemiValidation.ts
 *   - cashbackValidation.ts
 *   - dealValidation.ts
 *
 * @module formValidationUtils
 */

import { CampaignType } from 'src/@types/valueMatching/enums';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { toEpochMillis, parseAsins } from './formDataBuilder';

import { validateNcemi } from './validators/ncemiValidation';
import { validateCashback } from './validators/cashbackValidation';
import { validateBuyback } from './validators/buybackValidation';
import { validateDeal } from './validators/dealValidation';
import { validateInstantBankDiscount } from './validators/instantBankDiscountValidation';

// ============================================================================
// Public types
// ============================================================================

/** A translatable error: a key from `shared.campaignValidation` + optional interpolation params. */
export interface ValidationMessage {
    key: string;
    params?: Record<string, unknown>;
}

export interface ValidationResult {
    isValid: boolean;
    /** Error messages as translation keys + params. Translate at the call site. */
    errors: ValidationMessage[];
}

/** Per-field validation errors. Keys that have no error are omitted. */
export type FieldErrors = Partial<Record<FieldKey, ValidationMessage>>;

/* eslint-disable no-secrets/no-secrets */
export type FieldKey =
    | 'title'
    | 'asins'
    | 'startDateTime'
    | 'endDateTime'
    | 'sellerShare'
    | 'brandShare'
    | 'bankShare'
    | 'amazonShare'
    | 'fundingSharesSum'
    | 'brandName'
    | 'priceLeadingGuardrail'
    | 'termsAndConditions'
    | 'cashbackExtentGuardrail'
    | 'cashbackPercentLimitGuardrail'
    | 'cashbackAbsoluteLimitGuardrail'
    | 'buybackPercentOfAsinPrice'
    | 'buybackPercentOfBuybackOffer'
    | 'buybackAbsoluteValueCap'
    | 'dealPercentOfOriginalDealPrice'
    | 'suspendedAsins'
    | 'ibdBankNames'
    | 'ibdInstruments'
    | 'ibdEventType'
    | 'ibdTermsAndConditions'
    | 'ibdCampaignBudget'
    | 'ibdMaxTimesRewardAvailable'
    | 'ibdMaxRewardValuePerTransaction'
    | 'ibdMaxRewardValuePerPromotion'
    | 'ibdPercentExtentOfCompetitorGuardrail'
    | 'ibdPercentOfASINPriceGuardrail'
    | 'ibdAbsoluteValueGuardrail';
/* eslint-enable no-secrets/no-secrets */

// ============================================================================
// Constants
// ============================================================================

/**
 * Maximum ASIN counts per campaign type.
 * These limits are enforced by the backend and reflected here for early validation.
 * NCEMI/Cashback share the same limit (800); Buyback and Deal support larger lists.
 */
const MAX_ASIN_COUNTS: Record<CampaignType, number> = {
    [CampaignType.NO_COST_EMI]: 800,
    [CampaignType.CASHBACK]: 800,
    [CampaignType.BUYBACK]: 2000,
    [CampaignType.DEAL]: 4000,
    [CampaignType.INSTANT_BANK_DISCOUNT]: 2000,
};

/** Regex for validating ASIN format: 10-char alphanumeric starting with a letter, or 10-digit numeric with optional trailing X. */
const ASIN_REGEX = /^([0-9]{9}[0-9X]|[A-Z][A-Z0-9]{9})$/;

// ============================================================================
// Public API
// ============================================================================

export function getFieldErrors(
    form: UseCampaignFormResult,
    campaignType: CampaignType,
    formType: 'create' | 'modify',
    propagationDelayInMinutes: number,
): FieldErrors {
    const f: FieldErrors = {};

    // ── Common validators ───────────────────────────────────────────────
    validateTitle(form.title, f);
    validateAsins(form.asins, campaignType, f);
    validateStartDateTime(form.startDate, form.startTime, propagationDelayInMinutes, f);
    validateEndDateTime(form.endDate, form.endTime, form.startDate, form.startTime, f);

    // ── Campaign-type-specific validators (delegated to dedicated modules) ──
    switch (campaignType) {
        case CampaignType.NO_COST_EMI:
            validateNcemi(form, f);
            break;
        case CampaignType.CASHBACK:
            validateCashback(form, f);
            break;
        case CampaignType.BUYBACK:
            validateBuyback(form, f);
            break;
        case CampaignType.DEAL:
            validateDeal(form, f);
            break;
        case CampaignType.INSTANT_BANK_DISCOUNT:
            validateInstantBankDiscount(form, f);
            break;
    }

    // ── Modify-specific validators ──────────────────────────────────────
    if (formType === 'modify') {
        validateSuspendedAsins(form, f);
    }

    return f;
}

/**
 * Submit-time validation that returns a flat error list of `ValidationMessage`s.
 */
export function validateCampaignForm(
    form: UseCampaignFormResult,
    campaignType: CampaignType,
    formType: 'create' | 'modify',
    propagationDelayInMinutes: number,
): ValidationResult {
    const fieldErrors = getFieldErrors(form, campaignType, formType, propagationDelayInMinutes);
    const allErrors = Object.values(fieldErrors).filter((msg): msg is ValidationMessage => !!msg);
    const seen = new Set<string>();
    const errors = allErrors.filter((msg) => {
        if (seen.has(msg.key)) return false;
        seen.add(msg.key);
        return true;
    });
    return { isValid: errors.length === 0, errors };
}

// ============================================================================
// Common field validators
// ============================================================================

function validateTitle(title: string, f: FieldErrors): void {
    if (!title || title.trim().length === 0) {
        f.title = { key: 'title_empty' };
        return;
    }
    if (title.trim().length > 30) {
        f.title = { key: 'title_too_long' };
        return;
    }
    if (/\t/.test(title) || / {4,}/.test(title)) {
        f.title = { key: 'title_tab_characters' };
    }
}

function validateAsins(asinsText: string, campaignType: CampaignType, f: FieldErrors): void {
    const asinList = parseAsins(asinsText);

    if (asinList.length === 0) {
        f.asins = { key: 'asins_empty' };
        return;
    }

    const maxCount = MAX_ASIN_COUNTS[campaignType];
    if (asinList.length > maxCount) {
        f.asins = { key: 'asins_exceed_max', params: { maxCount, campaignType } };
        return;
    }

    const invalidAsins = asinList.filter((asin) => !ASIN_REGEX.test(asin));
    if (invalidAsins.length > 0) {
        f.asins = { key: 'asins_invalid_format', params: { invalidAsins: invalidAsins.join(', ') } };
    }
}

function validateStartDateTime(
    startDate: string,
    startTime: string,
    propagationDelayInMinutes: number,
    f: FieldErrors,
): void {
    if (!startDate || !startTime) {
        f.startDateTime = { key: 'start_date_empty' };
        return;
    }

    const startEpoch = toEpochMillis(startDate, startTime);
    if (startEpoch != null) {
        const minStartTime = Date.now() + propagationDelayInMinutes * 60 * 1000;
        if (startEpoch < minStartTime) {
            f.startDateTime = {
                key: 'start_time_too_soon',
                params: { delayMinutes: propagationDelayInMinutes },
            };
        }
    }
}

function validateEndDateTime(
    endDate: string,
    endTime: string,
    startDate: string,
    startTime: string,
    f: FieldErrors,
): void {
    if (!endDate || !endTime) {
        f.endDateTime = { key: 'end_date_empty' };
        return;
    }

    const endEpoch = toEpochMillis(endDate, endTime);
    const startEpoch = toEpochMillis(startDate, startTime);
    const now = Date.now();

    if (endEpoch != null) {
        if (endEpoch <= now || (startEpoch != null && endEpoch <= startEpoch)) {
            f.endDateTime = { key: 'end_time_before_start' };
        }
    }
}

// ============================================================================
// Modify-specific validators
// ============================================================================

// eslint-disable-next-line no-secrets/no-secrets
/**
 * Validates that suspended ASINs are a subset of the included ASINs (modify flow only).
 *
 * Note: `suspendedAsins` is an optional field added by the modify flow's form state.
 * It's not part of the base `UseCampaignFormResult` (create-only hook). We access it
 * via a type-safe interface extension rather than an untyped `Record<string, unknown>` cast.
 */
function validateSuspendedAsins(form: UseCampaignFormResult, f: FieldErrors): void {
    const suspendedText = (form as UseCampaignFormResult & { suspendedAsins?: string }).suspendedAsins;
    if (typeof suspendedText !== 'string' || !suspendedText.trim()) return;

    const includedList = parseAsins(form.asins);
    const suspendedList = parseAsins(suspendedText);
    if (suspendedList.length === 0) return;

    const includedSet = new Set(includedList);
    const invalidSuspended = suspendedList.filter((asin) => !includedSet.has(asin));

    if (invalidSuspended.length > 0) {
        f.suspendedAsins = {
            key: 'suspended_asins_not_included',
            params: { invalidAsins: invalidSuspended.join(', ') },
        };
    }
}
